import { PubSubEvents } from "@/types/PubSub";

import { pubSub } from "./PubSub";

/** 游戏标志量类 用于管理位标志（bit flags） */
class OverFlowFlagManager {
  private static instance: OverFlowFlagManager;

  public static getInstance(): OverFlowFlagManager {
    if (!OverFlowFlagManager.instance) {
      OverFlowFlagManager.instance = new OverFlowFlagManager();
    }
    return OverFlowFlagManager.instance;
  }
  /** 标志量（使用 BigInt 支持64位）- 非响应式 */
  private mValue: bigint = 0n;
  /** 下一个可用的位置 */
  private nextPosition: number = 0;
  /** 最大位数 */
  private static readonly MAX_BITS = 64;

  /** 标志量 */
  get Value(): bigint {
    return this.mValue;
  }

  set Value(value: bigint) {
    this.mValue = value;
  }

  constructor(flag?: bigint | number) {
    if (flag !== undefined) {
      this.mValue = typeof flag === "number" ? BigInt(flag) : flag;
      this.updateNextPosition();
    }
  }

  /**
   * 自动添加一个标志位（从0位开始）
   *
   * @returns 分配的位置
   */
  public addFlag(): number {
    const position = this.nextPosition;
    this.setBit(position);
    this.updateNextPosition();
    pubSub.publish(PubSubEvents.AddFlag, this.getBitCount());
    return position;
  }

  /**
   * 移除最左边的标志位（最高位）
   *
   * @returns 被移除的位置，如果没有标志位则返回 -1
   */
  public removeFlag(): number {
    const position = this.getHighestBitPosition();

    if (position === -1) {
      return -1; // 没有标志位可移除
    }

    this.mValue &= ~(1n << BigInt(position));
    this.updateNextPosition();
    pubSub.publish(PubSubEvents.RemoveFlag, this.getBitCount());
    return position;
  }

  /**
   * 获取最高位的1的位置
   *
   * @returns 最高位的位置，如果没有1则返回 -1
   */
  public getHighestBitPosition(): number {
    if (this.mValue === 0n) {
      return -1;
    }

    // 从最高位开始查找第一个1
    for (let i = OverFlowFlagManager.MAX_BITS - 1; i >= 0; i--) {
      if (this.isBitSet(i)) {
        return i;
      }
    }
    return -1;
  }

  /**
   * 设置指定位置为1
   *
   * @param position 位置（0-63）
   * @returns 更新后的标志量
   */
  public setBit(position: number): bigint {
    if (position < 0 || position >= OverFlowFlagManager.MAX_BITS) {
      throw new Error(
        `position must be between 0 and ${OverFlowFlagManager.MAX_BITS - 1}`,
      );
    }
    this.mValue |= 1n << BigInt(position);
    this.updateNextPosition();
    return this.mValue;
  }

  /**
   * 设置指定位置为0
   *
   * @param position 位置（0-63）
   * @returns 更新后的标志量
   */
  public clearBit(position: number): bigint {
    if (position < 0 || position >= OverFlowFlagManager.MAX_BITS) {
      throw new Error(
        `position must be between 0 and ${OverFlowFlagManager.MAX_BITS - 1}`,
      );
    }
    this.mValue &= ~(1n << BigInt(position));
    this.updateNextPosition();
    return this.mValue;
  }

  /**
   * 切换指定位置的值（0变1，1变0）
   *
   * @param position 位置（0-63）
   * @returns 更新后的标志量
   */
  public toggleBit(position: number): bigint {
    if (position < 0 || position >= OverFlowFlagManager.MAX_BITS) {
      throw new Error(
        `position must be between 0 and ${OverFlowFlagManager.MAX_BITS - 1}`,
      );
    }
    this.mValue ^= 1n << BigInt(position);
    this.updateNextPosition();
    return this.mValue;
  }

  /**
   * 检查指定位置是否为1
   *
   * @param position 位置（0-63）
   * @returns 是否为1
   */
  public isBitSet(position: number): boolean {
    if (position < 0 || position >= OverFlowFlagManager.MAX_BITS) {
      throw new Error(
        `position must be between 0 and ${OverFlowFlagManager.MAX_BITS - 1}`,
      );
    }
    return (this.mValue & (1n << BigInt(position))) !== 0n;
  }

  /**
   * 获取下一个可用的位置
   *
   * @returns 下一个可用位置
   */
  public getNextPosition(): number {
    return this.nextPosition;
  }

  /**
   * 获取所有设置为1的位置
   *
   * @returns 位置数组
   */
  public getSetBits(): number[] {
    const positions: number[] = [];
    for (let i = 0; i < OverFlowFlagManager.MAX_BITS; i++) {
      if (this.isBitSet(i)) {
        positions.push(i);
      }
    }
    return positions;
  }

  /**
   * 获取设置为1的位数
   *
   * @returns 位数
   */
  public getBitCount(): number {
    let count = 0;
    let value = this.mValue;
    while (value !== 0n) {
      count += Number(value & 1n);
      value >>= 1n;
    }
    return count;
  }

  /**
   * 重置所有位为0
   *
   * @returns 更新后的标志量
   */
  public reset(): bigint {
    this.mValue = 0n;
    this.nextPosition = 0;
    return 0n;
  }

  /**
   * 设置所有位为1
   *
   * @returns 更新后的标志量
   */
  public setAll(): bigint {
    this.mValue = -1n; // 等同于 0xFFFFFFFFFFFFFFFF
    this.nextPosition = OverFlowFlagManager.MAX_BITS; // 没有可用位置
    return this.mValue;
  }

  /**
   * 获取二进制字符串表示
   *
   * @returns 二进制字符串
   */
  public toBinaryString(): string {
    return this.mValue.toString(2).padStart(OverFlowFlagManager.MAX_BITS, "0");
  }

  /**
   * 获取十六进制字符串表示
   *
   * @returns 十六进制字符串
   */
  public toHexString(): string {
    return "0x" + this.mValue.toString(16).toUpperCase();
  }

  /**
   * 获取数值表示（如果不超过 Number.MAX_SAFE_INTEGER）
   *
   * @returns 数值
   */
  public toNumber(): number {
    if (this.mValue <= BigInt(Number.MAX_SAFE_INTEGER)) {
      return Number(this.mValue);
    }
    throw new Error("overflow Number.MAX_SAFE_INTEGER");
  }

  /** 更新下一个可用位置 */
  private updateNextPosition(): void {
    this.nextPosition = 0;
    while (
      this.nextPosition < OverFlowFlagManager.MAX_BITS &&
      this.isBitSet(this.nextPosition)
    ) {
      this.nextPosition++;
    }
  }
}
export const overFlowFlagManager = OverFlowFlagManager.getInstance();
